// What a placed field was placed AGAINST.
//
// CONSUMER: shared (Workers routes + node-pool tests).
//
// A saved map remembers where every field sits on the page and nothing
// about why it sits there. That is fine until the document changes: AAR
// publishes a new revision, a paragraph is inserted on page 2, and every
// coordinate below it now points at the wrong line. Coordinates alone
// cannot survive a revision, because the thing they are relative to —
// the page — is what changed.
//
// A caption can. "Buyer's Signature" is still called that in the new
// revision, just forty points lower, and the signature line is still the
// same distance from it. So this module answers one question at save
// time — which caption is this field attached to, and how far from it —
// and shared/esign/layout-carryover.ts answers the other one later:
// where did that caption go?
//
// Pure and dependency-free, the field-types.ts rule, so the Worker and
// the node-pool tests derive anchors identically. An anchor derived
// two different ways matches nothing.

import { bindingForLabel, type BindingLookup } from "./label-bindings";

/** A text run off the PDF, same shape the suggester already works in. */
export interface AnchorSpan {
  pageNumber: number;
  text: string;
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Where a field is attached, and how far from it. Every part of this is
 *  needed to find the field again on a document that has moved. */
export interface FieldAnchor {
  /** The caption, normalized (see normalizeAnchorText). */
  text: string;
  pageNumber: number;
  /** Which occurrence of this exact caption on this page, 0-based. */
  occurrence: number;
  /** Field origin minus caption origin, in PDF points. */
  dx: number;
  dy: number;
  /** What the label dictionary says the caption asks for, when it knows.
   *  The second-chance key when the wording itself changes. */
  token: string | null;
}

/** A field as it sits on the page, in the coordinate space every other
 *  e-sign module uses: PDF points, top-left origin, y down. */
export interface PlacedBox {
  pageNumber: number;
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Normalize a caption for comparison.
 *
 * The comparison IS the behavior here, so this is deliberately narrow
 * about what it forgives. It absorbs the differences between two EXPORTS
 * of the same words — case, whitespace, smart quotes, the trailing colon
 * a form prints after a label — and the blank itself, since the whole
 * point of a caption is that what follows it is empty on one copy and
 * filled on another.
 *
 * It does NOT absorb different words. "Buyer's Signature" and "Seller's
 * Signature" must stay different, or a carry-over would put one party's
 * signature on the other's line — which is the single worst thing this
 * feature could do, and it would look completely deliberate.
 *
 * Digits collapse to '#' for the same reason document-fingerprint.ts
 * does it: "Section 8" surviving a renumber to "Section 9" is the
 * difference between carrying a map over and remapping a contract by
 * hand. A section number is not what identifies a caption; its words are.
 */
export function normalizeAnchorText(raw: string): string {
  return raw
    .toLowerCase()
    // Quote and dash characters vary by exporter, not by content — same
    // set document-fingerprint.ts unifies, for the same reason.
    .replace(/["'‘’“”]/g, "'")
    .replace(/[–—]/g, "-")
    .replace(/ /g, " ")
    // The blank the caption labels. A ruled line and a typed run of
    // underscores are the same thing to a reader and must be to us.
    .replace(/_{2,}/g, " ")
    // Section and paragraph numbers renumber between revisions.
    .replace(/\d+/g, "#")
    .replace(/#(?:\s*[,./-]\s*#)+/g, "#")
    .replace(/\s+/g, " ")
    // Leading and trailing punctuation a form prints around a label.
    //
    // ASYMMETRIC, and that is the point. Leading strips '#' too, so the
    // section number this function just normalized ("3. Property
    // Address:" → "#. property address:") comes off and a renumbered
    // section is still the same caption. Trailing must NOT: a form that
    // captions a blank "Assessor's #:" is USING the hash as the word
    // "number", and stripping it leaves "assessor's", which the label
    // dictionary does not recognize — so the field arrives unbound and
    // a reworded caption can never be matched by what it asks for.
    // Found by the APN test.
    .replace(/^[\s:.,;·#*()-]+/, "")
    .replace(/[\s:.,;·*()-]+$/, "")
    .trim();
}

/** Below this a "caption" is punctuation or an artifact, not a label.
 *  Anchoring to a stray bullet would match anywhere on any document. */
const MIN_ANCHOR_CHARS = 3;

/** Above this it is a paragraph of legalese, not a caption. Same guard
 *  and same reasoning as label-bindings.ts's MAX_LABEL_CHARS, a little
 *  looser because an anchor is compared whole rather than pattern-matched
 *  — a long caption is still a usable key, a long paragraph is not. */
const MAX_ANCHOR_CHARS = 90;

/** How far above a field its caption may sit and still be its caption.
 *  About two lines of 10pt text. Beyond that they are not related; they
 *  just happen to be on the same page. */
const ABOVE_GAP_PT = 34;

/** How far left of a field its caption may sit on the same line. Wide
 *  enough for "Buyer's Signature:" plus the gap before a long ruled
 *  line, narrow enough not to reach across a two-column signature block
 *  and grab the other party's caption. */
const LEFT_GAP_PT = 260;

/**
 * Two boxes are on the same line when their vertical extents overlap at
 * all.
 *
 * DECISION: overlap, not a tolerance on their centres. A field is
 * routinely taller than the caption beside it — a 28pt signature box
 * next to a 10pt line of text — so their centres are legitimately 7-9pt
 * apart while the two plainly sit on one line. A centre tolerance tight
 * enough not to catch the NEXT line down was too tight to catch that,
 * and the field came back with no anchor at all (found by the repeated
 * initials-block test). Overlap is the physical question anyway: does
 * this caption run alongside this box?
 */
function onSameLine(
  span: { y: number; height: number },
  box: { y: number; height: number },
): boolean {
  return Math.min(span.y + span.height, box.y + box.height) - Math.max(span.y, box.y) > 0;
}

/** Is this run of text usable as a caption at all? */
export function isUsableAnchorText(normalized: string): boolean {
  if (normalized.length < MIN_ANCHOR_CHARS || normalized.length > MAX_ANCHOR_CHARS) return false;
  // Needs at least one letter. A run of '#' (a normalized number) or of
  // punctuation identifies nothing.
  return /[a-z]/.test(normalized);
}

interface Candidate {
  span: AnchorSpan;
  normalized: string;
  occurrence: number;
  score: number;
}

/**
 * The caption a box belongs to, or null.
 *
 * Two shapes, in preference order, and the order matters:
 *
 *   1. A caption to the LEFT on the same line — "Signature: ______".
 *      This is how forms label an inline blank, and it is unambiguous:
 *      there is one thing immediately left of a line.
 *   2. A caption ABOVE — the label printed over its ruled line, which is
 *      how AAR's signature blocks are drawn.
 *
 * Left beats above because a field with a caption on its own line has
 * been named explicitly, while the nearest thing above it might be the
 * last line of a paragraph. Within each shape, nearest wins.
 *
 * Returns null rather than reaching further when nothing is close. An
 * unanchored field is one that simply cannot be carried over — it comes
 * back as unmapped on the next revision, which is the fail-closed
 * outcome. A far-fetched anchor would carry the field to wherever that
 * unrelated text landed, and it would look placed.
 */
export function anchorForBox(
  box: PlacedBox,
  spans: readonly AnchorSpan[],
  lookup?: BindingLookup,
): FieldAnchor | null {
  const boxRight = box.x + box.width;

  // Occurrence is counted over the whole page in reading order, so the
  // number stored here means the same thing the carry-over will count on
  // the new document.
  const occurrences = new Map<string, number>();
  const candidates: Candidate[] = [];

  for (const span of pageSpansInReadingOrder(spans, box.pageNumber)) {
    const normalized = normalizeAnchorText(span.text);
    if (!isUsableAnchorText(normalized)) continue;
    const occurrence = occurrences.get(normalized) ?? 0;
    occurrences.set(normalized, occurrence + 1);

    const spanRight = span.x + span.width;

    // Shape 1: same line, to the left, not overlapping the field.
    if (onSameLine(span, box)) {
      const gap = box.x - spanRight;
      if (gap >= -2 && gap <= LEFT_GAP_PT) {
        // Nearest-left wins; the +1000 keeps every same-line candidate
        // ahead of every above candidate rather than letting a very
        // close paragraph line outrank a slightly distant label.
        candidates.push({ span, normalized, occurrence, score: 1000 - gap });
        continue;
      }
    }

    // Shape 2: above, horizontally overlapping the field.
    const gapAbove = box.y - (span.y + span.height);
    const overlaps = span.x <= boxRight && spanRight >= box.x;
    if (overlaps && gapAbove >= -2 && gapAbove <= ABOVE_GAP_PT) {
      candidates.push({ span, normalized, occurrence, score: 100 - gapAbove });
    }
  }

  if (candidates.length === 0) return null;
  candidates.sort((a, b) => b.score - a.score);
  const best = candidates[0]!;

  return {
    text: best.normalized,
    pageNumber: box.pageNumber,
    occurrence: best.occurrence,
    dx: round2(box.x - best.span.x),
    dy: round2(box.y - best.span.y),
    // Recorded from the caption's own words, so it is available even
    // when this field carries no merge token of its own — a signature
    // line under "Property Address" is anchored by the address caption
    // whether or not the signature merges anything.
    token: lookup ? (bindingForLabel(best.normalized, lookup)?.token ?? null) : null,
  };
}

/** Spans of one page, in the order a reader meets them. Sorted rather
 *  than trusted: occurrence numbering is only stable if both sides count
 *  in the same order, and extraction order has changed underneath us
 *  before (the page-by-page fix, 2026-09-02). */
export function pageSpansInReadingOrder(
  spans: readonly AnchorSpan[],
  pageNumber: number,
): AnchorSpan[] {
  return spans
    .filter((s) => s.pageNumber === pageNumber)
    .slice()
    .sort((a, b) => a.y - b.y || a.x - b.x);
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}
